import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSetting } from "../../context/appSettingContext";
import { useCountdown } from "../../hooks/CountDown";
import useMediaQuery from "../../hooks/useMediaQuery";
import close_icon from "../../images/icons/material-symbols_close.svg";
import qr_code from "../../images/icons/material-symbols_qr-code-2.svg";
import AnimatedPage from "../FramerMotion/AnimatedPage";
import Navbar from "../Navbar/Navbar";
import PromotionImageCarousel from "./PromotionImageCarousel";
import axiosInstance from "../../config/axiosInstance";

function SingleOffer({ baseURL, clientID, appSettings }) {
    const { theme } = useAppSetting();
    const { AdminCRMDataURL, AdminFileSrvURL } = appSettings;
    const { promoId, promoCode } = useParams();
    const isAbove600px = useMediaQuery("(min-width:600px)");

    const homeSection = useRef();
    const navigate = useNavigate();

    const [upperSectionClass, setUpperSectionClass] = useState("height-250");

    const [smallImageClass, setSmallImageClass] = useState("bottom-10");

    const [offerDetails, setOfferDetails] = useState(null);

    const [countdownDate, setCountdownDate] = useState(new Date());

    const [days, hours, minutes, seconds] = useCountdown(countdownDate);

    useEffect(() => {
        homeSection.current.addEventListener("scroll", (e) => {
            if (e.target.scrollTop > 30) {
                setUpperSectionClass("height-200");
                setSmallImageClass("bottom--25");
            } else if (e.target.scrollTop === 0) {
                setUpperSectionClass("height-250");
                setSmallImageClass("bottom-10");
            }
        });
        getPromotions(baseURL);
    }, []);

    const getPromotions = async (baseURL) => {
        const payLoad = {
            DATA: {
                AUTH_KEY: "TXlDb206TG95QVBJMTIz",
                CLIENT_ID: clientID, // updated CLIENT_ID
                LOC_ID: "ALL", // updated LOC_ID
                LOY_EXC: 0,
            },
        };

        try {
            const response = await axiosInstance.post(
                `${AdminCRMDataURL}LoyGetPromotions`,
                payLoad,
            );

            const data = response.data;

            if (data?.STATUS && Array.isArray(data.PROMOTIONS)) {
                // Try to find the offer initially
                const offer = data.PROMOTIONS.find(
                    (promotion) => promotion.PROMO_ID == promoId,
                );

                if (offer) {
                    setOfferDetails(offer);
                    setCountdownDate(new Date(offer.VALID_TO));
                } else {
                    console.error("Promotion not found with given promoId.");
                }
            } else {
                console.error(
                    "Failed to get promotions:",
                    data?.MESSAGE || "Unknown error",
                );
            }
        } catch (error) {
            console.error("Error fetching promotions:", error);
        }
    };

    const [loading, setLoading] = useState(false);
    const download = async (url, name) => {
        if (!url) {
            console.error("Download URL not provided");
            return;
        }
        setLoading(true);
        let tempLink; // Declare tempLink variable outside of conditional scope
        try {
            const request = await fetch(url);
            const blob = await request.blob();
            const blobURL = URL.createObjectURL(blob);

            // Check if it's iOS to give a prompt since behavior can vary
            const isIOS =
                /iPad|iPhone|iPod/.test(navigator.userAgent) &&
                !window.MSStream;

            if (isIOS) {
                // Construct alert message with the download link
                const alertMessage = `Press and hold the link below and select 'Save' to download:\n\n${blobURL}`;
                alert(alertMessage);

                // Optionally create a visible link in the document
                tempLink = document.createElement("a");
                tempLink.href = blobURL;
                tempLink.download = name;
                tempLink.textContent = "Download PDF"; // Provide a text link for iOS users
                tempLink.style.display = "block"; // Make it visible
                tempLink.style.marginTop = "10px"; // Add some spacing if needed
                document.body.appendChild(tempLink); // Append to body or a specific container
            } else {
                // For non-iOS devices, just click the anchor
                const a = document.createElement("a");
                a.href = blobURL;
                a.style.display = "none"; // Keep it hidden
                a.download = name;
                document.body.appendChild(a);
                a.click(); // Trigger download for non-iOS devices

                // Remove the main anchor tag for non-iOS devices
                a.remove();
            }

            // Cleanup
            URL.revokeObjectURL(blobURL);

            if (isIOS) {
                // Optionally, you can remove the temporary link after a short delay
                setTimeout(() => {
                    document.body.removeChild(tempLink);
                }, 5000); // Remove after 5 seconds or adjust as necessary
            }
        } catch (error) {
            console.error("Error during download:", error);
            alert("Download failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Improved share button handler
    const handleShare = async () => {
        if (!navigator.share) {
            console.warn("Share API is not supported in this browser.");
            return;
        }

        try {
            await navigator.share({
                title: "Flyer",
                text: "Check out this flyer!",
                url: imageURL + offerDetails.promo_flyer,
            });
        } catch (error) {
            if (error.name !== "AbortError") {
                console.error("Error sharing:", error);
            } else {
                console.log("User canceled the share");
            }
        }
    };

    const imageURL = AdminFileSrvURL;
    return (
        <AnimatedPage>
            <div
                className="w-100 h-95 bg-wafaa-white-3 overflow-y-scroll pb-5 transition-0-2-bounce"
                ref={homeSection}
            >
                {/* Close Button */}
                {isAbove600px && (
                    <div
                        onClick={() => navigate(-1)}
                        className="position-absolute top-10 right-10 z-1"
                    >
                        <img src={close_icon} alt="Close icon" />
                    </div>
                )}

                {/* Banner Carousel */}
                <PromotionImageCarousel
                    clientID={clientID}
                    promoCode={promoCode}
                    promoId={promoId}
                    AdminFileSrvURL={AdminFileSrvURL}
                    appSettings={appSettings}
                />

                {/* Content Section */}
                <div className="w-85 mx-auto mt-3">
                    {/* Date */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 640 640"
                            width="22"
                            height="22"
                            className="text-taupe"
                        >
                            <path
                                d="M224 64C241.7 64 256 78.3 256 96L256 128L384 128L384 96C384 78.3 398.3 64 416 64C433.7 64 448 78.3 448 96L448 128L480 128C515.3 128 544 156.7 544 192L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 192C96 156.7 124.7 128 160 128L192 128L192 96C192 78.3 206.3 64 224 64zM160 304L160 336C160 344.8 167.2 352 176 352L208 352C216.8 352 224 344.8 224 336L224 304C224 295.2 216.8 288 208 288L176 288C167.2 288 160 295.2 160 304zM288 304L288 336C288 344.8 295.2 352 304 352L336 352C344.8 352 352 344.8 352 336L352 304C352 295.2 344.8 288 336 288L304 288C295.2 288 288 295.2 288 304zM432 288C423.2 288 416 295.2 416 304L416 336C416 344.8 423.2 352 432 352L464 352C472.8 352 480 344.8 480 336L480 304C480 295.2 472.8 288 464 288L432 288zM160 432L160 464C160 472.8 167.2 480 176 480L208 480C216.8 480 224 472.8 224 464L224 432C224 423.2 216.8 416 208 416L176 416C167.2 416 160 423.2 160 432zM304 416C295.2 416 288 423.2 288 432L288 464C288 472.8 295.2 480 304 480L336 480C344.8 480 352 472.8 352 464L352 432C352 423.2 344.8 416 336 416L304 416zM416 432L416 464C416 472.8 423.2 480 432 480L464 480C472.8 480 480 472.8 480 464L480 432C480 423.2 472.8 416 464 416L432 416C423.2 416 416 423.2 416 432z"
                                fill="currentColor"
                            />
                        </svg>

                        <p
                            className="mb-0 text-taupe"
                            style={{
                                fontSize: "14px",
                            }}
                        >
                            valid through{" "}
                            <span
                                className="text-wafaa-black-4"
                                style={{
                                    fontWeight: "600",
                                }}
                            >
                                {moment(offerDetails?.VALID_TO || "").format(
                                    "DD MMM YYYY",
                                )}
                            </span>
                        </p>
                    </div>

                    {/* Title */}
                    <div
                        className="position-relative border-wafaa-periwinkle"
                        style={{
                            borderLeft: "4px solid",
                            left: -16,
                        }}
                    >
                        <h2
                            className="text-wafaa-black-4"
                            style={{
                                paddingLeft: "12px",
                                fontSize: "24px",
                                fontWeight: "700",
                                lineHeight: "32px",
                            }}
                        >
                            {offerDetails?.PROMO_DESC}
                        </h2>
                    </div>

                    {/* Description */}
                    <p
                        className="text-taupe-1"
                        style={{
                            fontSize: "16px",
                            lineHeight: "30px",
                        }}
                    >
                        {offerDetails?.DESC}
                    </p>
                </div>

                {/* Buttons */}
                <div
                    className="w-90 mx-auto mb-4"
                    style={{ paddingTop: "1rem" }}
                >
                    {offerDetails != null && offerDetails.FlyerPath !== "" && (
                        <>
                            {/* Top Buttons */}
                            <div
                                className="d-flex justify-content-between mb-3"
                                style={{ flexWrap: "wrap", gap: "12px" }}
                            >
                                {/* Download */}
                                <button
                                    disabled={loading}
                                    onClick={() =>
                                        download(
                                            imageURL + offerDetails.promo_flyer,
                                            "brochure",
                                        )
                                    }
                                    className="text-wafaa-white-2 bg-wafaa-royal-blue"
                                    style={{
                                        width: "48%",
                                        height: "42px",
                                        borderRadius: "12px",
                                        border: "none",
                                        fontWeight: "600",
                                        fontSize: "12px",
                                        flexGrow: 1,
                                    }}
                                >
                                    {!loading ? (
                                        <div
                                            className="d-flex align-items-center justify-content-center"
                                            style={{ gap: "5px" }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 640 640"
                                                width="18"
                                                height="18"
                                                className="text-wafaa-white-2"
                                                fill="currentColor"
                                            >
                                                <path d="M352 96C352 78.3 337.7 64 320 64C302.3 64 288 78.3 288 96L288 306.7L246.6 265.3C234.1 252.8 213.8 252.8 201.3 265.3C188.8 277.8 188.8 298.1 201.3 310.6L297.3 406.6C309.8 419.1 330.1 419.1 342.6 406.6L438.6 310.6C451.1 298.1 451.1 277.8 438.6 265.3C426.1 252.8 405.8 252.8 393.3 265.3L352 306.7L352 96zM160 384C124.7 384 96 412.7 96 448L96 480C96 515.3 124.7 544 160 544L480 544C515.3 544 544 515.3 544 480L544 448C544 412.7 515.3 384 480 384L433.1 384L376.5 440.6C345.3 471.8 294.6 471.8 263.4 440.6L206.9 384L160 384zM464 440C477.3 440 488 450.7 488 464C488 477.3 477.3 488 464 488C450.7 488 440 477.3 440 464C440 450.7 450.7 440 464 440z" />
                                            </svg>
                                            Download Brochure
                                        </div>
                                    ) : (
                                        <div className="spinner-border spinner-border-sm text-light" />
                                    )}
                                </button>

                                {/* View */}
                                {offerDetails.promo_flyer && (
                                    <button
                                        onClick={() =>
                                            window.open(
                                                imageURL +
                                                    offerDetails.promo_flyer,
                                                "_blank",
                                            )
                                        }
                                        className="bg-wafaa-white-2 text-wafaa-royal-blue border-wafaa-royal-blue"
                                        style={{
                                            width: "48%",
                                            height: "42px",
                                            borderRadius: "12px",
                                            border: "1.5px solid",
                                            fontWeight: "600",
                                            fontSize: "12px",
                                            flexGrow: 1,
                                        }}
                                    >
                                        <div
                                            className="d-flex align-items-center justify-content-center"
                                            style={{ gap: "5px" }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 640 640"
                                                width="18"
                                                height="18"
                                                className="text-wafaa-royal-blue"
                                                fill="currentColor"
                                            >
                                                <path d="M320 96C239.2 96 174.5 132.8 127.4 176.6C80.6 220.1 49.3 272 34.4 307.7C31.1 315.6 31.1 324.4 34.4 332.3C49.3 368 80.6 420 127.4 463.4C174.5 507.1 239.2 544 320 544C400.8 544 465.5 507.2 512.6 463.4C559.4 419.9 590.7 368 605.6 332.3C608.9 324.4 608.9 315.6 605.6 307.7C590.7 272 559.4 220 512.6 176.6C465.5 132.9 400.8 96 320 96zM176 320C176 240.5 240.5 176 320 176C399.5 176 464 240.5 464 320C464 399.5 399.5 464 320 464C240.5 464 176 399.5 176 320zM320 256C320 291.3 291.3 320 256 320C244.5 320 233.7 317 224.3 311.6C223.3 322.5 224.2 333.7 227.2 344.8C240.9 396 293.6 426.4 344.8 412.7C396 399 426.4 346.3 412.7 295.1C400.5 249.4 357.2 220.3 311.6 224.3C316.9 233.6 320 244.4 320 256z" />
                                            </svg>
                                            View Brochure
                                        </div>
                                    </button>
                                )}

                                {/* Share */}
                                {navigator.share && (
                                    <button
                                        onClick={handleShare}
                                        className="text-wafaa-black-4 bg-wafaa-white-2 border-wafaa-white-5"
                                        style={{
                                            width: "48%",
                                            height: "42px",
                                            borderRadius: "12px",
                                            border: "1px solid",
                                            fontWeight: "600",
                                            fontSize: "12px",
                                            flexGrow: 1,
                                        }}
                                    >
                                        <div
                                            className="d-flex align-items-center justify-content-center"
                                            style={{ gap: "5px" }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 640 640"
                                                width="18"
                                                height="18"
                                                className="text-wafaa-royal-blue"
                                                fill="currentColor"
                                            >
                                                <path d="M448 256C501 256 544 213 544 160C544 107 501 64 448 64C395 64 352 107 352 160C352 165.4 352.5 170.8 353.3 176L223.6 248.1C206.7 233.1 184.4 224 160 224C107 224 64 267 64 320C64 373 107 416 160 416C184.4 416 206.6 406.9 223.6 391.9L353.3 464C352.4 469.2 352 474.5 352 480C352 533 395 576 448 576C501 576 544 533 544 480C544 427 501 384 448 384C423.6 384 401.4 393.1 384.4 408.1L254.7 336C255.6 330.8 256 325.5 256 320C256 314.5 255.5 309.2 254.7 304L384.4 231.9C401.3 246.9 423.6 256 448 256z" />
                                            </svg>
                                            Share Brochure
                                        </div>
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Countdown Card */}
                <div className="w-90 mx-auto mb-4">
                    <div
                        className="p-3 bg-wafaa-white-2 border-wafaa-white-5"
                        style={{
                            borderRadius: "22px",
                            boxShadow: "0px 4px 15px rgba(0,0,0,0.06)",
                            border: "1px solid",
                        }}
                    >
                        <div className="d-flex align-items-center gap-2 mb-4">
                            <svg
                                className="text-wafaa-indigo"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M12 8V12L15 15"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="9"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                />
                            </svg>

                            <p
                                className="mb-0 text-wafaa-black-4"
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                }}
                            >
                                current offers end on{" "}
                                <span
                                    style={{
                                        fontWeight: "400",
                                    }}
                                >
                                    {moment(offerDetails?.VALID_TO).format(
                                        "DD MMM YYYY",
                                    )}
                                </span>
                            </p>
                        </div>

                        <div className="d-flex justify-content-between gap-2">
                            {[
                                { label: "DAYS", value: days },
                                { label: "HOURS", value: hours },
                                { label: "MINUTES", value: minutes },
                                { label: "SECONDS", value: seconds },
                            ].map((item, index) => (
                                <div
                                    key={index}
                                    className="bg-taupe-2"
                                    style={{
                                        flex: 1,
                                        borderRadius: "14px",
                                        padding: "14px 6px",
                                        textAlign: "center",
                                    }}
                                >
                                    <h3
                                        className="text-wafaa-black-4"
                                        style={{
                                            fontSize: "34px",
                                            margin: 0,
                                            fontWeight: "400",
                                        }}
                                    >
                                        {String(item.value).padStart(2, "0")}
                                    </h3>

                                    <p
                                        className="text-taupe-1"
                                        style={{
                                            fontSize: "11px",
                                            margin: 0,
                                            marginTop: "4px",
                                            letterSpacing: "1px",
                                        }}
                                    >
                                        {item.label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <Navbar activePage="home" />
        </AnimatedPage>
    );
}

export default SingleOffer;
